import React from 'react';
import DropdownSelect from './DropdownSelect';
import { Icon } from './Icon';

/**
 * Standard page-navigation pagination bar (shared by tenant + admin).
 *
 * The single standard list-pagination control: an optional page-size selector
 * (default 20/50/200) + a *"Mostrando X a Y de Z"* count + prev/next page
 * navigation (*"Página X de Y"*). Renders **outside** the table/grid card (as a
 * sibling below it). Returns `null` when the list is empty.
 *
 * Presentational only — it does not own page state. Pair it with the
 * `usePagination` hook for client-side lists, or wire `onPageChange` to a
 * `skip`/`limit` fetch for backend-paged lists. Text is passed via `labels`
 * so the component stays i18n-agnostic: the tenant wraps it with `useLocale`
 * (`tenant/frontend/components/common/Pagination.tsx`); admin passes Spanish
 * literals. Hide the page-size selector by omitting `onPageSizeChange`.
 */
export interface PaginationLabels {
  /** Suffix after the page-size dropdown, e.g. "por página". */
  perPage: string;
  /** Count prefix, e.g. "Mostrando". */
  showing: string;
  /** Range separator, e.g. "a" (X a Y). */
  to: string;
  /** Count / page-of divider, e.g. "de". */
  of: string;
  /** Page-of prefix, e.g. "Página". */
  page: string;
  /** Accessible names. Optional — Spanish defaults, the tenant passes `t()`. */
  prevPage?: string;
  nextPage?: string;
  /** `<nav aria-label>` of the whole bar. */
  nav?: string;
  /** Accessible name of the page-size picker. */
  pageSize?: string;
}

export interface PaginationProps {
  /** 1-based current page. */
  page: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  /** Omit to hide the page-size selector (e.g. a fixed backend page size). */
  onPageSizeChange?: (size: number) => void;
  /** Page-size options. Standard = [20, 50, 200]. */
  pageSizeOptions?: number[];
  labels: PaginationLabels;
  /** Disable nav buttons (e.g. while a backend page is loading). */
  disabled?: boolean;
  /** Extra utilities merged into the bar wrapper. */
  className?: string;
}

const STANDARD_PAGE_SIZES = [20, 50, 200];

const navButtonClass =
  'p-1.5 rounded-md border border-border-subtle dark:border-border-subtle-dark text-slate-500 hover:bg-slate-50 dark:hover:bg-surface-hover-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors';

const Pagination: React.FC<PaginationProps> = ({
  page,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = STANDARD_PAGE_SIZES,
  labels,
  disabled = false,
  className = '',
}) => {
  if (totalItems <= 0) return null;

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const current = Math.min(Math.max(1, page), totalPages);
  const from = (current - 1) * pageSize + 1;
  const to = Math.min(current * pageSize, totalItems);

  return (
    <nav aria-label={labels.nav ?? 'Paginación'} className={`flex flex-col md:flex-row items-center justify-between gap-4 mt-4 ${className}`.trim()}>
      {onPageSizeChange ? (
        <div className="flex items-center gap-2">
          <div className="w-20">
            <DropdownSelect
              value={String(pageSize)}
              options={pageSizeOptions.map(n => ({ value: String(n), label: String(n) }))}
              onChange={(v: string) => onPageSizeChange(Number(v))}
              dropdownWidth="w-24"
              ariaLabel={labels.pageSize ?? `${labels.perPage}`}
            />
          </div>
          <span className="text-sm text-slate-500 dark:text-slate-400">{labels.perPage}</span>
        </div>
      ) : (
        <span />
      )}

      <span className="text-sm text-slate-500 dark:text-slate-400">
        {labels.showing} {from} {labels.to} {to} {labels.of} {totalItems}
      </span>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onPageChange(current - 1)}
          disabled={disabled || current <= 1}
          aria-label={labels.prevPage ?? 'Página anterior'}
          className={navButtonClass}
        >
          <Icon name="chevron_left" className="!text-[20px]" aria-hidden="true" />
        </button>
        <span className="text-sm font-medium px-2 text-slate-700 dark:text-slate-300" aria-live="polite">
          {labels.page} {current} {labels.of} {totalPages}
        </span>
        <button
          type="button"
          onClick={() => onPageChange(current + 1)}
          disabled={disabled || current >= totalPages}
          aria-label={labels.nextPage ?? 'Página siguiente'}
          className={navButtonClass}
        >
          <Icon name="chevron_right" className="!text-[20px]" aria-hidden="true" />
        </button>
      </div>
    </nav>
  );
};

export default Pagination;
